import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor, EditorOutput, EditorOutputProps } from 'ui-components';
import { captureException } from '~/errorsLogging';

import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { isEditorEmpty } from '../utils/editorUtils';

export interface EditableRichTextViewProps {
  editorState: string;
  readonly?: boolean;
}

export const EditableRichTextView = ({
  editorState,
  id,
  readonly = false,
}: EditableRichTextViewProps & { id: string }) => {
  const [content, setContent] =
    useState<NonNullable<EditorOutputProps['editor']>>();
  const boxRef = useRef<HTMLDivElement>(null);
  const { updateBlock, removeBlock, editingBlockId, setEditingBlockId } =
    usePageBuilderStore();

  const isEditing = editingBlockId === id;

  // Use a ref to track mouse interactions across renders
  const mouseDownStartedInEditorRef = useRef(false);

  // Initialize content from editorState and set edit mode for new blocks
  useEffect(() => {
    if (editorState) {
      try {
        const parsedState = JSON.parse(editorState) as NonNullable<
          EditorOutputProps['editor']
        >;
        setContent(parsedState);
      } catch (error) {
        captureException(error);
        // eslint-disable-next-line no-console
        console.error('Failed to parse editor state:', error);
      }
    } else if (!readonly) {
      // Auto-enter edit mode for new blocks (no editorState means it's new)
      setEditingBlockId(id);
    }
  }, [editorState, id, readonly, setEditingBlockId]);

  const handleChange = useCallback(
    (_html: string, editor: any) => {
      if (readonly) return;
      const serialized = editor.toJSON();
      setContent(serialized);
      updateBlock(id, {
        id,
        component: 'EditableRichTextView',
        properties: {
          editorState: JSON.stringify(serialized),
        },
      });
    },
    [id, updateBlock, readonly],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (readonly) return;
      // Only enter edit mode if clicking on the content area
      if (!isEditing) {
        setEditingBlockId(id);
        e.stopPropagation();
      }
    },
    [id, setEditingBlockId, readonly, isEditing],
  );

  // Handle exiting edit mode or removing block
  const handleExitEdit = useCallback(() => {
    if (readonly) return;

    if (isEditorEmpty(content)) {
      removeBlock(id);
    } else {
      setEditingBlockId(null);
    }
  }, [content, id, readonly, removeBlock, setEditingBlockId]);

  const shouldShowEditor = isEditing && !readonly;

  useEffect(() => {
    if (!shouldShowEditor) return;

    const ignoreElements = ['[data-floating-ui-portal]', '[role="dialog"]'];
    const handleMouseDown = (e: MouseEvent) => {
      const isDownEditorChild = boxRef.current?.contains(e.target as Node);
      const isDownInsideIgnoreElements = ignoreElements.some(
        (element) =>
          e.target instanceof HTMLElement && e.target.closest(element),
      );

      mouseDownStartedInEditorRef.current =
        isDownEditorChild || isDownInsideIgnoreElements;
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (mouseDownStartedInEditorRef.current) {
        mouseDownStartedInEditorRef.current = false;
        return;
      }

      const isUpEditorChild = boxRef.current?.contains(e.target as Node);
      const isUpInsideIgnoreElements = ignoreElements.some(
        (element) =>
          e.target instanceof HTMLElement && e.target.closest(element),
      );

      if (!isUpEditorChild && !isUpInsideIgnoreElements) {
        handleExitEdit();
      }
      mouseDownStartedInEditorRef.current = false;
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [shouldShowEditor, handleExitEdit]);

  useEffect(() => {
    if (!shouldShowEditor) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleExitEdit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [shouldShowEditor, handleExitEdit]);

  return (
    <Box
      width="full"
      overflowX="auto"
      overflowY="hidden"
      ref={boxRef}
      as="section"
      cursor={readonly ? 'default' : isEditing ? 'text' : 'pointer'}
      onClick={handleClick}
      tabIndex={readonly ? undefined : 0}
      data-block-id={id}
      background={isEditing ? 'primary.25!important' : ''}
      color={isEditing ? '#000' : undefined} // Editor has a white background hence we need to set the color to black
      role="region"
      aria-label={isEditing ? 'Text editor' : 'Text content'}
      aria-describedby={`rich-text-${id}`}
      rounded={6}
    >
      {shouldShowEditor ? (
        <Editor
          variant="advanced"
          initialValue={content}
          onChange={handleChange}
          minHeight="150px"
          placeholder="Click to Add Text"
          aria-label="Rich text editor"
        />
      ) : (
        <EditorOutput
          editor={content}
          stylesOverride={{
            root: {
              color: 'var(--media-kit-text-base)',
            },
            h1: {
              color: 'var(--media-kit-text-base)',
            },
            h2: {
              color: 'var(--media-kit-text-base)',
            },
            h3: {
              color: 'var(--media-kit-text-base)',
            },
            paragraph: {
              color: 'var(--media-kit-text-base)',
            },
          }}
        />
      )}
    </Box>
  );
};
